import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { UserPlus, CheckCircle, Lock } from 'lucide-react';

/**
 * "Claim your account" prompt shown on the Order Confirmation page when the
 * customer submitted their order as a guest. Flips them into a full account
 * in one click so future reorders live under the same email + saved history.
 *
 * Self-contained: calls useAuth().claimAccount which updates the stored
 * user (is_guest→false) so the card automatically disappears on success.
 */
export const ClaimAccountCard = () => {
  const { user, isGuest, claimAccount } = useAuth();
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [claimed, setClaimed] = useState(false);

  if (!isGuest && !claimed) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setBusy(true);
    setError(null);
    const r = await claimAccount({
      password,
      first_name: firstName.trim(),
      last_name: lastName.trim(),
    });
    setBusy(false);
    if (!r.success) {
      setError(r.error || 'Could not claim account.');
      return;
    }
    setClaimed(true);
  };

  if (claimed) {
    return (
      <div
        className="mt-6 p-5 bg-green-50 border-2 border-green-300 rounded-lg flex items-start gap-3"
        data-testid="claim-account-success"
      >
        <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-green-900">Account created!</p>
          <p className="text-sm text-green-800">
            You can now log in anytime with <strong>{user?.email}</strong> to see your orders and reorder in one click.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="mt-6 p-6 bg-gradient-to-br from-amber-50 via-white to-[#4A7C59]/5 border-2 border-amber-300 rounded-lg shadow-sm text-left"
      data-testid="claim-account-card"
    >
      <div className="flex items-center gap-2 mb-3">
        <UserPlus className="w-5 h-5 text-[#4A7C59]" />
        <h3 className="text-lg font-bold text-gray-800">
          Save your order for 1-click reorders
        </h3>
      </div>
      <p className="text-sm text-gray-600 mb-4">
        You're checked out as a guest. Set a password below and you'll be able to log in anytime at{' '}
        <strong>{user?.email}</strong> to see this order, track its status, and reorder without typing anything twice.
      </p>

      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="claim-first-name" className="text-xs font-semibold text-gray-700">
              First name
            </Label>
            <Input
              id="claim-first-name"
              data-testid="claim-account-first-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              autoComplete="given-name"
              maxLength={60}
            />
          </div>
          <div>
            <Label htmlFor="claim-last-name" className="text-xs font-semibold text-gray-700">
              Last name
            </Label>
            <Input
              id="claim-last-name"
              data-testid="claim-account-last-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              autoComplete="family-name"
              maxLength={60}
            />
          </div>
        </div>
        <div>
          <Label htmlFor="claim-password" className="text-xs font-semibold text-gray-700">
            Create a password
          </Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <Input
              id="claim-password"
              data-testid="claim-account-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters"
              className="pl-10"
              autoComplete="new-password"
              minLength={6}
              maxLength={128}
              required
            />
          </div>
        </div>

        {error && (
          <p className="text-xs text-red-600" data-testid="claim-account-error">
            {error}
          </p>
        )}

        <Button
          type="submit"
          disabled={busy || !password}
          data-testid="claim-account-submit"
          className="w-full bg-[#4A7C59] hover:bg-[#3d6849] text-white"
        >
          {busy ? 'Creating account…' : 'Save my account'}
        </Button>

        <p className="text-[11px] text-gray-500 text-center">
          No spam, ever. Just your order history and faster reorders.
        </p>
      </form>
    </div>
  );
};
